import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextField, InputAdornment, IconButton, Tooltip } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import {
  keyboardEventToKey,
  captureShortcutKeys,
  normalizeShortcut,
  formatShortcutDisplay,
} from "../../../../util/src/blueprint/shortcut-keys.js";
import dico from "../../../../util/src/language/dico.js";

interface ShortcutCaptureProps {
  shortcutKey1: string;
  shortcutKey2?: string;
  shortcutKey3?: string;
  onChange: (key1: string, key2?: string, key3?: string) => void;
  disabled?: boolean;
}

/**
 * Captures a 1-to-3 key shortcut on focus. While the field is focused,
 * the component installs global document listeners in capture phase so
 * modifier-based shortcuts like `ctrl+a` or `meta+k` are intercepted
 * before the browser handles them.
 */
const ShortcutCaptureComponent = ({
  shortcutKey1,
  shortcutKey2,
  shortcutKey3,
  onChange,
  disabled,
}: ShortcutCaptureProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingKeys, setEditingKeys] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pressedKeysRef = useRef<Set<string>>(new Set());
  const editingKeysRef = useRef<string[]>([]);
  const behaviorDico = dico().behavior;
  const crDico = (behaviorDico.events as unknown as Record<string, unknown>)
    .controlRoom as Record<string, string>;

  const combo = normalizeShortcut(shortcutKey1, shortcutKey2, shortcutKey3);
  const [displayedCommittedCombo, setDisplayedCommittedCombo] = useState(combo);

  const beginEditing = useCallback(() => {
    pressedKeysRef.current.clear();
    editingKeysRef.current = [];
    setEditingKeys([]);
    setIsEditing(true);
  }, []);

  const stopEditing = useCallback(() => {
    pressedKeysRef.current.clear();
    editingKeysRef.current = [];
    setEditingKeys([]);
    setIsEditing(false);
  }, []);

  const commitCombo = useCallback(
    (keys: string[]) => {
      const nextCombo = normalizeShortcut(keys[0] ?? "", keys[1], keys[2]);
      setDisplayedCommittedCombo(nextCombo);
      const parts = nextCombo.split("+").filter(Boolean).slice(0, 3);
      onChange(parts[0] ?? "", parts[1], parts[2]);
    },
    [onChange],
  );

  const editingCombo = normalizeShortcut(
    editingKeys[0] ?? "",
    editingKeys[1],
    editingKeys[2],
  );

  const displayValue = editingCombo
    ? formatShortcutDisplay(editingCombo)
    : isEditing
      ? crDico.shortcutEditing
      : displayedCommittedCombo
        ? formatShortcutDisplay(displayedCommittedCombo)
        : crDico.shortcutEmpty;

  useEffect(() => {
    setDisplayedCommittedCombo(combo);
  }, [combo]);

  useEffect(() => {
    editingKeysRef.current = editingKeys;
  }, [editingKeys]);

  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const key = keyboardEventToKey(e);
      if (!key) return;
      if (
        !pressedKeysRef.current.has(key) &&
        editingKeysRef.current.length >= 3
      ) {
        return;
      }
      pressedKeysRef.current.add(key);
      if (editingKeysRef.current.includes(key)) return;
      const nextKeys = captureShortcutKeys(e, editingKeysRef.current);
      editingKeysRef.current = nextKeys;
      setEditingKeys(nextKeys);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const key = keyboardEventToKey(e);
      if (key) {
        pressedKeysRef.current.delete(key);
      }
      if (editingKeysRef.current.length === 0) return;
      commitCombo(editingKeysRef.current);
      stopEditing();
    };

    const handleClick = (e: MouseEvent) => {
      if (inputRef.current?.contains(e.target as Node)) return;
      stopEditing();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, [commitCombo, isEditing, stopEditing]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      stopEditing();
      setDisplayedCommittedCombo("");
      onChange("", undefined, undefined);
    },
    [onChange, stopEditing],
  );

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      value={displayValue}
      onFocus={beginEditing}
      onClick={beginEditing}
      onBlur={stopEditing}
      disabled={disabled}
      fullWidth
      inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
      sx={{
        "& .MuiInputBase-input": { py: 0.45, px: 0.75, fontSize: 12 },
      }}
      InputProps={{
        endAdornment: combo ? (
          <InputAdornment position="end">
            <Tooltip title={crDico.shortcutClear}>
              <IconButton size="small" onClick={handleClear} edge="end">
                <BackspaceIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

export const ShortcutCapture = React.memo(ShortcutCaptureComponent);
